import type { DocumentCitation, ProjectCitation } from "@/api/q-and-a";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type AnswerGroupCitation = (DocumentCitation | ProjectCitation) & {
  retrieval_date: string;
  project_id: string;
};

export interface AnswerGroupData {
  text: string;
  explanation?: string;
  citations: AnswerGroupCitation[];
  groupId: string;
}

export interface CitationsByProject {
  projectId: string;
  projectName: string;
  answerGroups: AnswerGroupData[];
}

const isDocumentCitation = (
  citation: AnswerGroupCitation,
): citation is DocumentCitation & { project_id: string; retrieval_date: string } =>
  !citation.extracted_from_project;

/**
 * Shared citation block component for displaying citation text
 * Used in both the citations modal table and the docviewer answer pane
 */
export interface CitationBlockProps {
  /** The citation text to display */
  text: string;
  /** Optional page number to display above the citation */
  pageNumber?: number | null;
  /** Click handler for the citation */
  onClick?: () => void;
  /** Optional className for customization */
  className?: string;
}

export const CitationBlock: React.FC<CitationBlockProps> = ({
  text,
  pageNumber,
  onClick,
  className = "",
}) => {
  return (
    <div className={className}>
      {pageNumber != null && (
        <div className="mb-1 text-xs text-muted-foreground">(Page {pageNumber})</div>
      )}
      <button
        onClick={onClick}
        className="block w-full cursor-pointer text-left text-sm"
        disabled={!onClick}
      >
        <div
          className={`border-l-4 border-green-600 bg-green-50 p-3 ${
            onClick ? "transition-colors hover:bg-green-100" : ""
          }`}
        >
          {text}
        </div>
      </button>
    </div>
  );
};

interface AnswerCitationsProps {
  /** Variable name to display in header */
  variableName?: string;
  /** Question text shown in tooltip when hovering variable name */
  questionText?: string;
  /** Citations grouped by project */
  citationsByProject: CitationsByProject[];
  /** Callback when user clicks to view a document */
  showDocument: ({
    id,
    filename,
    projectId,
    target,
  }: {
    id: string;
    filename: string;
    projectId?: string;
    target?: {
      groupId: string;
      citationId?: string;
    };
  }) => void;
}

/**
 * Table row for a single citation
 * Supports rowspan for both Value and Document columns
 */
const CitationRow: React.FC<{
  citation: AnswerGroupCitation;
  isFirstInGroup: boolean;
  groupRowSpan: number;
  valueText: string;
  isFirstInDocument: boolean;
  documentRowSpan: number;
  onDocumentClick?: () => void;
  onCitationClick?: () => void;
}> = ({
  citation,
  isFirstInGroup,
  groupRowSpan,
  valueText,
  isFirstInDocument,
  documentRowSpan,
  onDocumentClick,
  onCitationClick,
}) => {
  const isDocCitation = isDocumentCitation(citation);
  const documentName = isDocCitation
    ? citation.filename || "Unknown Document"
    : "[Project Data]";
  const pageNumber = isDocCitation ? citation.page : null;

  return (
    <tr className="border-b border-border last:border-b-0">
      {isFirstInGroup && (
        <td
          rowSpan={groupRowSpan}
          className="border-r border-border px-4 py-3 align-top font-medium"
        >
          {valueText}
        </td>
      )}
      {isFirstInDocument && (
        <td
          rowSpan={documentRowSpan}
          className="border-r border-border px-4 py-3 align-top"
        >
          <button
            onClick={onDocumentClick}
            className="max-w-[200px] truncate text-left text-linkblue hover:underline"
            title={documentName}
          >
            {documentName}
          </button>
        </td>
      )}
      <td className="border-r border-border px-4 py-3 text-center align-top text-muted-foreground">
        {pageNumber ?? "—"}
      </td>
      <td className="px-4 py-3 align-top">
        {citation.citation ? (
          <CitationBlock text={citation.citation} onClick={onCitationClick} />
        ) : (
          <span className="text-sm text-muted-foreground/70">No citation text</span>
        )}
      </td>
    </tr>
  );
};

/**
 * Table for a single project's citations
 */
const ProjectCitationsTable: React.FC<{
  projectName: string;
  answerGroups: AnswerGroupData[];
  showDocument: AnswerCitationsProps["showDocument"];
  isSticky: boolean;
}> = ({ projectName, answerGroups, showDocument, isSticky }) => {
  const navigate = useNavigate();

  // Deduplicate and sort citations within each answer group
  const processedGroups = answerGroups.map((group) => {
    const seen = new Set<string>();
    const uniqueCitations = group.citations.filter((citation) => {
      // Include page in dedup key so citations from different pages are kept
      const key = `${citation.citation ?? ""}::${citation.document_id ?? citation.source_project ?? ""}::${citation.page ?? ""}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    // Sort citations by document name, then by page number (ascending)
    const sortedCitations = uniqueCitations.sort((a, b) => {
      const isDocA = isDocumentCitation(a);
      const isDocB = isDocumentCitation(b);

      // Document citations come before project citations
      if (isDocA && !isDocB) return -1;
      if (!isDocA && isDocB) return 1;

      if (isDocA && isDocB) {
        // Sort by document name first
        const nameA = a.filename || "";
        const nameB = b.filename || "";
        const nameCompare = nameA.localeCompare(nameB);
        if (nameCompare !== 0) return nameCompare;

        // Then sort by page number (ascending)
        const pageA = a.page ?? Infinity;
        const pageB = b.page ?? Infinity;
        return pageA - pageB;
      }

      return 0;
    });

    return { ...group, citations: sortedCitations };
  });

  // Check if there are any citations across all groups
  const hasCitations = processedGroups.some(
    (group) => group.citations.length > 0,
  );

  return (
    <div className="mb-6">
      {/* Project Header */}
      <div
        className={`bg-white py-2 font-semibold ${
          isSticky ? "sticky top-0 z-10" : ""
        }`}
      >
        {projectName}
      </div>

      {!hasCitations ? (
        <div className="text-sm text-muted-foreground">
          No answers available for this project
        </div>
      ) : (
        /* Citations Table */
        <table className="w-full border-collapse border border-border text-sm">
          <thead>
            <tr className="border-b border-border bg-muted">
              <th className="w-[20%] border-r border-border px-4 py-2 text-left font-medium text-muted-foreground">
                Value
              </th>
              <th className="w-[20%] border-r border-border px-4 py-2 text-left font-medium text-muted-foreground">
                Document
              </th>
              <th className="w-[60px] border-r border-border px-4 py-2 text-center font-medium text-muted-foreground">
                Page
              </th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                Citation
              </th>
            </tr>
          </thead>
          <tbody>
            {processedGroups.map((group) => {
              const citations = group.citations;
              if (citations.length === 0) return null;

              // Group citations by document for rowspan calculation
              const getDocumentKey = (cit: AnswerGroupCitation) => {
                const isDoc = isDocumentCitation(cit);
                return isDoc
                  ? cit.document_id
                  : `project-${cit.source_project}`;
              };

              // Build document groups to calculate rowspans
              const documentGroups: { key: string; count: number }[] = [];
              let currentDocKey: string | null = null;
              citations.forEach((cit) => {
                const docKey = getDocumentKey(cit);
                if (docKey !== currentDocKey) {
                  documentGroups.push({ key: docKey, count: 1 });
                  currentDocKey = docKey;
                } else {
                  documentGroups[documentGroups.length - 1].count++;
                }
              });

              // Track position for determining first-in-document
              let documentGroupIndex = 0;
              let citationsInCurrentDoc = 0;

              return citations.map((citation, idx) => {
                const isDocCitation = isDocumentCitation(citation);
                const isFirstInGroup = idx === 0;
                const isFirstInDocument = citationsInCurrentDoc === 0;
                const documentRowSpan =
                  documentGroups[documentGroupIndex]?.count ?? 1;

                // Update tracking for next iteration
                citationsInCurrentDoc++;
                if (citationsInCurrentDoc >= documentRowSpan) {
                  documentGroupIndex++;
                  citationsInCurrentDoc = 0;
                }

                const handleDocumentClick = () => {
                  if (isDocCitation) {
                    showDocument({
                      id: citation.document_id,
                      filename: citation.filename,
                      projectId: citation.project_id,
                      target: {
                        groupId: group.groupId,
                        citationId: citation.id,
                      },
                    });
                  } else {
                    navigate(`/projects/${citation.source_project}/documents`);
                  }
                };

                return (
                  <CitationRow
                    key={`${group.groupId}-${citation.id ?? idx}`}
                    citation={citation}
                    isFirstInGroup={isFirstInGroup}
                    groupRowSpan={citations.length}
                    valueText={group.text || "[Not found]"}
                    isFirstInDocument={isFirstInDocument}
                    documentRowSpan={documentRowSpan}
                    onDocumentClick={handleDocumentClick}
                    onCitationClick={handleDocumentClick}
                  />
                );
              });
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};;;

/**
 * Citations display component with table layout
 * Shows citations grouped by project with sticky headers
 */
export const AnswerCitations: React.FC<AnswerCitationsProps> = ({
  variableName,
  questionText,
  citationsByProject,
  showDocument,
}) => {
  const displayName = variableName || questionText || "Citations";
  const tooltipText = questionText || variableName;

  return (
    <div className="flex max-h-[70vh] flex-col p-2">
      {/* Header with variable name - stays fixed */}
      <div className="flex-shrink-0 border-b border-border bg-white pb-3 pr-8">
        <h2 className="text-lg font-semibold text-muted-foreground">
          Citations for{" "}
          {tooltipText ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help font-normal italic text-blue-900">
                    {displayName}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-md whitespace-normal">
                  {tooltipText}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="font-normal italic text-blue-900">
              {displayName}
            </span>
          )}
        </h2>
      </div>

      {/* Scrollable project sections */}
      <div className="flex-1 overflow-y-auto">
        {citationsByProject.map((project) => (
          <ProjectCitationsTable
            key={project.projectId}
            projectName={project.projectName}
            answerGroups={project.answerGroups}
            showDocument={showDocument}
            isSticky={true}
          />
        ))}

        {citationsByProject.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            No citations available
          </div>
        )}
      </div>
    </div>
  );
};
